<template>
    <div class="relay-radios">
        <div
            class="form-group form-group-header no-clearfix"
            data-test-id="relay-radios-header"
        >
            <span>Radios</span>
            <span
                class="relay-radios__count"
                data-test-id="relay-radios-count"
                ><span data-test-id="relay-radios-count-current">{{ activeRadios.length }}</span> of
                <span data-test-id="relay-radios-count-max">{{ maxRadios }}</span></span
            >
        </div>

        <p
            v-if="!modelValue.length"
            class="relay-radios__empty"
            data-test-id="relay-radios-empty"
        >
            No radios on this relay yet. Search below to add one.
        </p>

        <div
            v-for="(radio, index) in modelValue"
            :key="radio.urn ?? index"
            class="relay-radios__card"
            :class="{ 'relay-radios__card--removed': radio.ui_removed }"
            :data-test-id="`relay-radio-${index}`"
        >
            <div class="relay-radios__body">
                <div class="relay-radios__heading">
                    <!-- A CSS mask, not an <img>, so the black line-art icon takes the card's text colour. -->
                    <span
                        v-if="radio.icon"
                        class="relay-radios__icon"
                        :style="{ '--relay-radio-icon': `url('${radio.icon}')` }"
                        aria-hidden="true"
                        :data-test-id="`relay-radio-${index}-icon`"
                    ></span>
                    <span
                        class="relay-radios__serial"
                        :data-test-id="`relay-radio-${index}-serial`"
                        >{{ radio.serial_number }}</span
                    >
                    <button
                        v-if="radio.ui_removed"
                        type="button"
                        class="relay-radios__action"
                        :disabled="disabled"
                        title="Put this radio back"
                        aria-label="Put this radio back"
                        :data-test-id="`relay-radio-${index}-restore-button`"
                        @click="restore(index)"
                    >
                        <i class="pi pi-undo"></i>
                    </button>
                    <button
                        v-else
                        type="button"
                        class="relay-radios__action relay-radios__action--delete"
                        :disabled="disabled"
                        title="Remove this radio"
                        aria-label="Remove this radio"
                        :data-test-id="`relay-radio-${index}-delete-button`"
                        @click="remove(index)"
                    >
                        <i class="pi pi-trash"></i>
                    </button>
                </div>

                <!-- The removal note shares the label's line so the card doesn't grow when it appears. -->
                <div class="relay-radios__name-line">
                    <label
                        :for="`relay-radio-${index}-name`"
                        class="relay-radios__name-label"
                        >Name</label
                    >
                    <span
                        v-if="radio.ui_removed"
                        class="relay-radios__removed-note"
                        :data-test-id="`relay-radio-${index}-removed-note`"
                        >Will be removed on save</span
                    >
                </div>
                <input
                    :id="`relay-radio-${index}-name`"
                    :value="radio.name"
                    :disabled="disabled || radio.ui_removed"
                    :required="!radio.ui_removed"
                    :data-test-id="`relay-radio-${index}-name`"
                    type="text"
                    maxlength="191"
                    minlength="3"
                    @input="rename(index, $event.target.value)"
                />
            </div>
        </div>

        <p
            v-if="limitMessage"
            class="relay-radios__limit"
            data-test-id="relay-radios-limit-message"
        >
            {{ limitMessage }}
        </p>

        <div class="form-group relay-radios__add">
            <radio-typeahead
                v-if="canAdd"
                :selected-urns="selectedRadioUrns"
                :current-assembly-id="currentAssemblyId"
                :disabled="disabled"
                :validation-message="requiredMessage"
                data-test-id-prefix="radio-selector"
                @add="add"
            />
            <p
                v-else
                class="relay-radios__at-limit"
                data-test-id="relay-radios-at-limit"
            >
                This relay already has {{ maxRadiosText }}. Delete one to add another.
            </p>
        </div>
    </div>
</template>

<script>
import 'primeicons/primeicons.css'
import RadioTypeahead from '@/components/RadioTypeahead.vue'

/**
 * @param {object} radio a row on the form
 * @param {object} row a chosen suggestion
 * @returns {boolean}
 */
function isSameRadio(radio, row) {
    return Boolean(radio.urn) && radio.urn === row.urn
}

export default {
    name: 'RelayRadios',

    components: {
        RadioTypeahead,
    },

    props: {
        // Rows keyed by `urn`. UI-only keys: ui_removed (struck out, restorable)
        // and ui_original (came with the relay, so removal is reversible).
        modelValue: {
            required: true,
            type: Array,
        },
        maxRadios: {
            required: false,
            type: Number,
            default: 2,
        },
        // A radio removed from this list stays attached to this assembly server-side until save.
        currentAssemblyId: {
            required: false,
            type: Number,
            default: null,
        },
        disabled: {
            required: false,
            type: Boolean,
            default: false,
        },
    },

    emits: ['update:modelValue'],

    data() {
        return {
            limitMessage: '',
        }
    },

    computed: {
        activeRadios() {
            return this.modelValue.filter((radio) => !radio.ui_removed)
        },

        canAdd() {
            return this.activeRadios.length < this.maxRadios
        },

        maxRadiosText() {
            return this.maxRadios === 1 ? '1 radio' : `${this.maxRadios} radios`
        },

        // The forms submit natively, so this is set as the search field's validity
        // message. With no radios the field is always shown.
        requiredMessage() {
            return this.activeRadios.length ? '' : 'Please add at least one radio.'
        },

        selectedRadioUrns() {
            return this.activeRadios.map((radio) => radio.urn).filter(Boolean)
        },
    },

    methods: {
        rename(index, name) {
            this.replace(index, { ...this.modelValue[index], name })
        },

        add(row) {
            this.limitMessage = ''

            // A removed radio is still attached server-side, so search can offer
            // it back; choosing it restores the original row and name.
            const existing = this.modelValue.findIndex((radio) => isSameRadio(radio, row))

            // Already on the form (offered when unavailable radios are shown): no-op.
            if (existing !== -1 && !this.modelValue[existing].ui_removed) {
                return
            }

            if (existing !== -1) {
                this.restore(existing)
                return
            }

            this.$emit('update:modelValue', [
                ...this.modelValue,
                {
                    urn: row.urn,
                    name: null,
                    // A radio's suggestion label is its serial.
                    serial_number: row.name,
                    icon: row.icon ?? null,
                    ui_removed: false,
                    ui_original: false,
                },
            ])
        },

        /**
         * Original radios are struck out so they can be restored; ones added this session are dropped.
         *
         * @param {number} index
         */
        remove(index) {
            this.limitMessage = ''
            const radio = this.modelValue[index]

            if (!radio.ui_original) {
                this.$emit(
                    'update:modelValue',
                    this.modelValue.filter((_, position) => position !== index)
                )
                return
            }

            this.replace(index, { ...radio, ui_removed: true })
        },

        restore(index) {
            if (!this.canAdd) {
                this.limitMessage = `A relay can have at most ${this.maxRadiosText}. Delete another radio first.`
                return
            }

            this.limitMessage = ''
            this.replace(index, { ...this.modelValue[index], ui_removed: false })
        },

        replace(index, radio) {
            this.$emit(
                'update:modelValue',
                this.modelValue.map((existing, position) => (position === index ? radio : existing))
            )
        },
    },
}
</script>

<style lang="scss" scoped>
.relay-radios {
    margin-bottom: 30px;

    // Matches the other section headers in the relay forms.
    .form-group-header {
        font-size: 15px;
        font-weight: 800;
        font-family: Avenir, Helvetica, Arial, sans-serif;
        height: 24px;
    }

    // Body font, not the header's.
    &__count {
        font-family: 'Avenir Book';
        font-weight: normal;
        float: right;
        font-size: 0.8rem;
        color: #bec3dc;
    }

    &__empty,
    &__at-limit {
        font-size: 0.875rem;
        color: #999;
        margin: 0 0 15px;
    }

    &__card {
        display: flex;
        align-items: center;
        gap: 12px;
        padding: 12px;
        margin-bottom: 12px;
        border: 1px solid #636a6d;
        border-radius: 6px;

        // Serial left uncoloured: recolouring it reads as an error, not a pending change.
        &--removed {
            border-color: #f96a62;
        }
    }

    &__body {
        flex: 1 1 auto;
        min-width: 0;

        // No global box-sizing, so the full-width input would overflow the card.
        input {
            box-sizing: border-box;
        }
    }

    &__heading {
        display: flex;
        align-items: center;
        gap: 8px;
    }

    // The SVG is only the mask; the fill is background-color, so it follows `color`.
    &__icon {
        flex: 0 0 auto;
        width: 24px;
        height: 24px;
        color: #bec3dc;
        background-color: currentColor;
        -webkit-mask: var(--relay-radio-icon) center / contain no-repeat;
        mask: var(--relay-radio-icon) center / contain no-repeat;
    }

    // Plain text rather than a disabled input, since the serial can't be edited.
    &__serial {
        display: block;
        flex: 1 1 auto;
        min-width: 0;
        font-family: 'Avenir Book';
        font-size: 1rem;
        color: #fff;
        overflow-wrap: anywhere;
    }

    &__name-line {
        display: flex;
        align-items: baseline;
        justify-content: space-between;
        gap: 8px;
        margin-top: 12px;
        margin-bottom: 4px;
    }

    // Overrides the form's label width, which would wrap the note onto a second line.
    &__name-label {
        width: auto;
        margin: 0;
        font-family: 'Avenir Heavy';
        font-size: 0.8rem;
        color: #999;
    }

    &__removed-note {
        font-size: 0.8rem;
        color: #f96a62;
        white-space: nowrap;
    }

    &__action {
        flex: 0 0 auto;
        display: grid;
        place-items: center;
        width: 28px;
        height: 28px;
        border: 0;
        border-radius: 5px;
        background: transparent;
        color: #bec3dc;
        font-size: 1rem;
        cursor: pointer;

        &:hover:enabled {
            background: #3a4245;
            color: #fff;
        }

        &--delete:hover:enabled {
            background: #f96a62;
            color: #fff;
        }

        &:disabled {
            cursor: not-allowed;
            opacity: 0.5;
        }
    }

    &__limit {
        font-size: 0.875rem;
        color: #f96a62;
        margin: 0 0 15px;
    }

    &__add {
        margin-top: 20px;
        padding-top: 20px;
        border-top: 1px solid #636a6d;
    }
}
</style>
